/*
 * The pharmacy's own pages: the overview, and the care homes.
 *
 * The overview answers one question — what is waiting on the pharmacy today? — so it only shows
 * things the pharmacy can act on: changes the homes have reported, drafts waiting to be approved,
 * deliveries sitting unbooked, doses that could not be given, and deadlines coming up or missed.
 *
 * Care homes lists every home and is where new ones are added. Opening a home shows the same screen
 * the home's own staff see, with the pharmacy's tabs.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';
import type { CalendarEvent } from './pharmacy-calendar.tsx';
import { addDays, shortDay, todayKey } from './days.ts';
import type { Go } from './pharmacy-routes.ts';

type HomeCard = {
  id: string;
  name: string;
  supplyMode: string;
  residents: number;
  today: { total: number; recorded: number; overdue: number; notGiven: number };
  // changes: the pharmacy's drafts not yet approved. reported: what the home has told the pharmacy.
  waiting: {
    changes: number; reported: number; deliveries: number; urgentTasks: number; stockProblems: number; cdMismatch: number;
  };
  cycle: { number: number; day: number; days: number; endsOn: string; nextStartsOn: string; orderBy: string } | null;
};

function useHomeCards() {
  const [homes, setHomes] = useState<HomeCard[] | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ homes: HomeCard[] }>('/api/pharmacy/homes');
      setHomes(data.homes);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the homes.');
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(), 120_000);
    return () => window.clearInterval(timer);
  }, [load]);

  return { homes, error, load };
}

function needsFor(home: HomeCard): number {
  return home.waiting.changes + home.waiting.reported + home.waiting.deliveries + home.waiting.urgentTasks +
    home.waiting.stockProblems + home.waiting.cdMismatch;
}

/* ---------- Home: the overview ---------- */

export function OverviewPage({ go }: { go: Go }) {
  const { homes, error, load } = useHomeCards();
  const [events, setEvents] = useState<CalendarEvent[] | null>(null);

  /*
   * Two weeks back as well as two ahead: a deadline missed last week is the most important thing on
   * this page, and it would vanish from a list that started today.
   */
  const loadEvents = useCallback(async () => {
    const today = todayKey();
    try {
      const data = await api.get<{ events: CalendarEvent[] }>(
        `/api/pharmacy/calendar?from=${addDays(today, -14)}&to=${addDays(today, 13)}`,
      );
      setEvents(data.events.filter((e) => e.state === 'late' || e.state === 'upcoming'));
    } catch {
      setEvents([]);
    }
  }, []);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  const totals = (homes ?? []).reduce(
    (sum, home) => ({
      changes: sum.changes + home.waiting.changes,
      reported: sum.reported + home.waiting.reported,
      deliveries: sum.deliveries + home.waiting.deliveries,
      notGiven: sum.notGiven + home.today.notGiven,
      problems: sum.problems + home.waiting.stockProblems + home.waiting.cdMismatch,
    }),
    { changes: 0, reported: 0, deliveries: 0, notGiven: 0, problems: 0 },
  );

  const attention = (homes ?? []).filter((home) => needsFor(home) || home.today.overdue || home.today.notGiven);
  const late = (events ?? []).filter((e) => e.state === 'late');
  const upcoming = (events ?? []).filter((e) => e.state === 'upcoming');

  return (
    <section>
      <div className="head">
        <div>
          <h1>Overview</h1>
          <p className="muted">What is waiting on the pharmacy today, across every home.</p>
        </div>
        <button type="button" className="quiet" onClick={() => { void load(); void loadEvents(); }}>Refresh</button>
      </div>

      {error ? <p className="error" role="alert">{error}</p> : null}

      {homes && !homes.length ? (
        <div className="empty">
          <h2>Start by adding a care home</h2>
          <p className="muted">
            Everything here — the rounds, medicine changes, deliveries and the calendar — belongs to a home.
            Add the first one and it will fill in.
          </p>
          <button type="button" onClick={() => go({ page: 'homes', adding: true })}>Add a care home</button>
        </div>
      ) : null}

      <div className="live-summary">
        <div className="stat"><b>{totals.reported}</b><span>Changes reported by homes</span></div>
        <div className="stat"><b>{totals.changes}</b><span>Drafts to approve</span></div>
        <div className="stat"><b>{totals.deliveries}</b><span>Deliveries not booked in</span></div>
        <div className="stat"><b>{totals.notGiven}</b><span>Doses not given today</span></div>
        <div className="stat"><b>{totals.problems}</b><span>Stock and register problems</span></div>
      </div>

      {homes === null ? <p className="muted">Loading…</p> : null}

      {homes && homes.length ? (
        <div className="overview-cols">
          <div>
            <h2>Needs attention</h2>
            {attention.length ? (
              <ul className="list">
                {attention.map((home) => (
                  <li key={home.id}>
                    <button
                      type="button"
                      className="row column"
                      // Reported changes and drafts are dealt with on the residents' pages.
                      onClick={() => go({
                        page: 'home',
                        homeId: home.id,
                        tab: home.waiting.reported || home.waiting.changes ? 'residents' : 'dashboard',
                        deliveryId: null,
                      })}
                    >
                      <span className="row-main">{home.name}</span>
                      <HomeChips home={home} />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">Nothing is waiting on the pharmacy at any home.</p>
            )}
          </div>

          <div>
            <h2>Deadlines</h2>
            {events === null ? <p className="muted">Loading…</p> : null}
            {events && !late.length && !upcoming.length ? (
              <p className="muted">Nothing due in the next two weeks.</p>
            ) : null}
            <ul className="list">
              {[...late, ...upcoming].slice(0, 12).map((e) => (
                <li key={`${e.homeId}-${e.kind}-${e.date}`}>
                  <button
                    type="button"
                    className="row"
                    onClick={() => go({ page: 'home', homeId: e.homeId, tab: 'cycles', deliveryId: null })}
                  >
                    <span>
                      <span className="row-main">{e.label}</span>
                      <span className="muted small"> · {e.homeName}</span>
                    </span>
                    <span className={`chip${e.state === 'late' ? ' warn' : ''}`}>
                      {e.state === 'late' ? `Late · ${shortDay(e.date)}` : shortDay(e.date)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" className="link" onClick={() => go({ page: 'calendar', month: null })}>
              See the calendar
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function HomeChips({ home }: { home: HomeCard }) {
  const needs = needsFor(home);
  return (
    <div className="chips">
      {home.today.overdue ? <span className="chip warn">{home.today.overdue} overdue</span> : null}
      {home.today.notGiven ? <span className="chip off">{home.today.notGiven} not given</span> : null}
      {home.waiting.reported ? <span className="chip warn">{home.waiting.reported} change{home.waiting.reported === 1 ? '' : 's'} reported</span> : null}
      {home.waiting.changes ? <span className="chip">{home.waiting.changes} draft{home.waiting.changes === 1 ? '' : 's'} to approve</span> : null}
      {home.waiting.deliveries ? <span className="chip warn">{home.waiting.deliveries} to book in</span> : null}
      {home.waiting.stockProblems ? <span className="chip off">{home.waiting.stockProblems} out of stock</span> : null}
      {home.waiting.cdMismatch ? <span className="chip off">{home.waiting.cdMismatch} register mismatch</span> : null}
      {home.waiting.urgentTasks ? <span className="chip warn">{home.waiting.urgentTasks} urgent</span> : null}
      {!needs && !home.today.overdue && !home.today.notGiven ? <span className="chip done">Nothing waiting</span> : null}
    </div>
  );
}

/* ---------- Care homes ---------- */

export function CareHomesPage({
  go, adding, onAdded,
}: { go: Go; adding: boolean; onAdded: (homeId: string) => void | Promise<void> }) {
  const { homes, error } = useHomeCards();

  return (
    <section>
      <div className="head">
        <div>
          <h1>Care homes</h1>
          <p className="muted">
            {homes === null ? 'Every home the pharmacy supplies.' : `${homes.length} home${homes.length === 1 ? '' : 's'}.`}
          </p>
        </div>
        {!adding ? (
          <button type="button" onClick={() => go({ page: 'homes', adding: true })}>Add a care home</button>
        ) : null}
      </div>

      {error ? <p className="error" role="alert">{error}</p> : null}

      {adding ? <AddHomeForm onCancel={() => go({ page: 'homes', adding: false })} onAdded={onAdded} /> : null}

      {homes === null ? <p className="muted">Loading…</p> : null}
      {homes && !homes.length && !adding ? (
        <div className="empty">
          <h2>No care homes yet</h2>
          <p className="muted">Add the first home the pharmacy supplies. Its residents and staff are set up from inside it.</p>
          <button type="button" onClick={() => go({ page: 'homes', adding: true })}>Add a care home</button>
        </div>
      ) : null}

      <div className="live-grid">
        {(homes ?? []).map((home) => {
          const needs = needsFor(home);
          return (
            <button
              key={home.id}
              type="button"
              className={`live-card ${needs ? 'overdue' : home.today.total ? 'done' : 'none'}`}
              onClick={() => go({ page: 'home', homeId: home.id, tab: null, deliveryId: null })}
            >
              <span className="row-main">{home.name}</span>
              <p className="muted small">
                {home.residents} resident{home.residents === 1 ? '' : 's'} ·{' '}
                {home.supplyMode === 'tray' ? 'trays' : 'original packs'}
              </p>
              <p className="live-count">{home.today.recorded} of {home.today.total} today</p>
              <HomeChips home={home} />
              {home.cycle ? (
                <p className="muted small">
                  Cycle {home.cycle.number}, day {home.cycle.day} of {home.cycle.days} · order by {shortDay(home.cycle.orderBy)}
                </p>
              ) : (
                <p className="muted small">No cycle set up yet.</p>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
}

/*
 * Nothing edits a home once it is added yet, so everything that shapes its medicines is asked for
 * now — with the usual answers filled in, so a typical home is a name and a date.
 */
function AddHomeForm({ onCancel, onAdded }: { onCancel: () => void; onAdded: (homeId: string) => void | Promise<void> }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [supplyMode, setSupplyMode] = useState<'tray' | 'original'>('tray');
  const [cycleStartOn, setCycleStartOn] = useState('');
  const [cycleDays, setCycleDays] = useState(28);
  const [orderLeadDays, setOrderLeadDays] = useState(7);
  const [trayCapacity, setTrayCapacity] = useState(7);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const firstOrder = cycleStartOn ? addDays(cycleStartOn, -orderLeadDays) : '';

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const data = await api.post<{ id: string }>('/api/pharmacy/homes', {
        name: name.trim(),
        address: address.split('\n').map((line) => line.trim()).filter(Boolean),
        phone: phone.trim(),
        supplyMode,
        cycleStartOn: cycleStartOn || null,
        cycleDays,
        orderLeadDays,
        trayCapacity,
      });
      await onAdded(data.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add the home.');
      setBusy(false);
    }
  };

  return (
    <form className="card wide add-home" onSubmit={submit}>
      <h2>Add a care home</h2>

      <label htmlFor="home-name">Name</label>
      <input id="home-name" type="text" required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />

      <label htmlFor="home-address">Address</label>
      <textarea
        id="home-address"
        rows={3}
        placeholder="One line per line of the address"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
      />

      <label htmlFor="home-phone">Phone</label>
      <input id="home-phone" type="text" inputMode="tel" maxLength={40} value={phone} onChange={(e) => setPhone(e.target.value)} />

      <fieldset>
        <legend>How the medicines are supplied</legend>
        <label className="check" htmlFor="supply-tray">
          <input id="supply-tray" type="radio" name="supply" checked={supplyMode === 'tray'} onChange={() => setSupplyMode('tray')} />
          <span>In trays (monitored dosage system)</span>
        </label>
        <label className="check" htmlFor="supply-original">
          <input id="supply-original" type="radio" name="supply" checked={supplyMode === 'original'} onChange={() => setSupplyMode('original')} />
          <span>In original packs</span>
        </label>
        {supplyMode === 'tray' ? (
          <>
            <label htmlFor="tray-capacity">Medicines per tray before it is split into two</label>
            <input
              id="tray-capacity" type="number" min={1} max={14} required
              value={trayCapacity} onChange={(e) => setTrayCapacity(Number(e.target.value))}
            />
          </>
        ) : null}
      </fieldset>

      <fieldset>
        <legend>The supply cycle</legend>
        <label htmlFor="cycle-start">First cycle starts</label>
        <input id="cycle-start" type="date" value={cycleStartOn} onChange={(e) => setCycleStartOn(e.target.value)} />
        <p className="muted small">Usually a Monday. Leave it empty if it has not been agreed yet.</p>

        <div className="pair">
          <div>
            <label htmlFor="cycle-days">Days in a cycle</label>
            <input
              id="cycle-days" type="number" min={7} max={56} required
              value={cycleDays} onChange={(e) => setCycleDays(Number(e.target.value))}
            />
          </div>
          <div>
            <label htmlFor="order-lead">Order due, days before</label>
            <input
              id="order-lead" type="number" min={1} max={28} required
              value={orderLeadDays} onChange={(e) => setOrderLeadDays(Number(e.target.value))}
            />
          </div>
        </div>
        {firstOrder ? (
          <p className="muted small">The first order will be due on {shortDay(firstOrder)}.</p>
        ) : null}
      </fieldset>

      <p className="notice">Check the cycle before adding: the app cannot change a home's details afterwards yet.</p>

      {error ? <p className="error" role="alert">{error}</p> : null}
      <div className="row-actions">
        <button type="submit" disabled={busy}>{busy ? 'Adding…' : 'Add care home'}</button>
        <button type="button" className="quiet" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}
